import React from 'react'
import styles from './UsageCard.module.css'
import UsageGauge from './UsageGauge'

const UsageCard = ({
  isPayg = true,
  freeUsedUp = false,   // PAYG: free 50 exhausted → "of 100" billing state
  used = 0,
  total = null,
  rate = 0,
  monthlyFee = 0,       // SMB: plan price for the "$X / month" display
  planName = '',        // SMB: plan label
  periodStart = '',
  periodEnd = '',
  freeMinutes = 50,
  onWhatsThis,
}) => {
  // "What's this?" hover tooltip — explains what the card shows, with the real
  // (dynamic) numbers: PAYG rate / free allowance / 100-min gauge, or the SMB
  // plan's price + included minutes.
  const gaugeOf = total != null ? total : (isPayg ? (freeUsedUp ? 100 : freeMinutes) : 0)
  const tooltipText = isPayg
    ? (freeUsedUp
      ? `Your first ${freeMinutes} minutes are used. Now billed at $${Number(rate).toFixed(2)}/min. The gauge shows minutes used out of ${gaugeOf} this billing cycle.`
      : `You're on PAYG. Your first ${freeMinutes} minutes are free. The gauge shows minutes used out of ${gaugeOf} this billing cycle.`)
    : `You're on the ${planName || 'monthly'} plan at $${Number(monthlyFee).toFixed(2)}/month with ${gaugeOf} included minutes. The gauge shows minutes used out of ${gaugeOf} this billing cycle.`
  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <h3 className={styles.title}>Current Usage</h3>
        {/* Hover tooltip (CSS-only): the box shows on :hover of this wrapper. */}
        <span className={styles.whatsThisWrap}>
          <button type="button" className={styles.whatsThis} onClick={onWhatsThis}>
            <span className={styles.qMark}>?</span> What's this ?
          </button>
          <span className={styles.tooltip} role="tooltip">{tooltipText}</span>
        </span>
      </div>
      <p className={styles.subtitle}>
        {isPayg
          ? "You're on PAYG- pay only for what you use."
          : `You're on the ${planName || 'monthly'} plan.`}
      </p>

      <div className={styles.body}>
        <div className={styles.bodyRow}>
          <UsageGauge used={used} total={total} />

          <div className={styles.info}>
            <p className={styles.billedLabel}>Billed at</p>
            <p className={styles.billedValue}>
              {isPayg ? (
                <>
                  ${rate.toFixed(2)} <span className={styles.perMin}>/ min</span>
                </>
              ) : (
                <>
                  ${Number(monthlyFee).toFixed(2)} <span className={styles.perMin}>/ month</span>
                </>
              )}
            </p>
            <div className={styles.period}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="5" width="16" height="15" rx="2" stroke="#6524EB" strokeWidth="1.7" />
                <path d="M4 9h16M8 3v4M16 3v4" stroke="#6524EB" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
              <div>
                <span className={styles.periodLabel}>Billing period</span>
                <span className={styles.periodValue}>{periodStart && periodEnd ? `${periodStart} - ${periodEnd}` : '—'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <span className={styles.footerIcon}>
            <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.86697 7.48314C2.69834 9.11622 3.32295 10.7317 4.54665 11.8254C4.95824 12.1782 5.19899 12.6908 5.20896 13.2333V15.0993C5.21118 15.8415 5.72041 16.4872 6.44152 16.6603C6.61348 17.3825 7.25917 17.8928 8.00138 17.8928C8.74469 17.8928 9.38926 17.3825 9.56123 16.6603C10.2835 16.4872 10.7927 15.8426 10.7949 15.0993V13.2854C10.8038 12.7152 11.0545 12.176 11.4839 11.8021C12.7353 10.6549 13.3433 8.9664 13.1092 7.28573C12.874 5.60494 11.8278 4.1471 10.3112 3.38623C8.79352 2.62627 6.99964 2.66067 5.51283 3.47942C4.02619 4.29707 3.03658 5.79478 2.86685 7.48343L2.86697 7.48314ZM9.94286 15.0992C9.94175 15.5175 9.60338 15.857 9.18511 15.8581H6.81761C6.39935 15.857 6.06097 15.5175 6.06097 15.0992V14.3426H9.94286V15.0992ZM10.8737 4.78926C11.7812 5.60026 12.3026 6.75627 12.3092 7.97332C12.3159 9.18924 11.8078 10.3519 10.9092 11.1719C10.3023 11.7077 9.95061 12.4754 9.94286 13.2853V13.4906H6.06097V13.2332C6.04877 12.4499 5.70596 11.7099 5.11686 11.1941C4.26703 10.4297 3.75669 9.36019 3.69569 8.21959C3.63467 7.08022 4.02963 5.96192 4.79291 5.11306C5.5562 4.26324 6.62567 3.75179 7.76624 3.69189C8.90672 3.63088 10.025 4.02598 10.8737 4.78926Z" fill="#6524EB" />
              <path d="M8.42626 1.84608V0.426019C8.42626 0.190822 8.23543 0 8.00024 0C7.76504 0 7.57422 0.190822 7.57422 0.426019V1.84608C7.57422 2.08128 7.76504 2.2721 8.00024 2.2721C8.23543 2.2721 8.42626 2.08128 8.42626 1.84608Z" fill="#6524EB" />
              <path d="M2.94539 2.34018C2.86662 2.25698 2.75678 2.20927 2.64252 2.20705C2.52714 2.20594 2.4173 2.25032 2.33521 2.33242C2.25422 2.41341 2.20985 2.52435 2.21096 2.63861C2.21207 2.75399 2.25977 2.86271 2.34298 2.94259L3.34702 3.94663C3.4269 4.02984 3.53562 4.07755 3.651 4.07865C3.76527 4.08087 3.87621 4.03539 3.95719 3.9544C4.03929 3.87341 4.08367 3.76247 4.08256 3.6482C4.08145 3.53282 4.03263 3.42299 3.94943 3.34422L2.94539 2.34018Z" fill="#6524EB" />
              <path d="M1.84608 7.57422H0.426019C0.190822 7.57422 0 7.76504 0 8.00024C0 8.23543 0.190822 8.42626 0.426019 8.42626H1.84608C2.08128 8.42626 2.2721 8.23543 2.2721 8.00024C2.2721 7.76504 2.08128 7.57422 1.84608 7.57422Z" fill="#6524EB" />
              <path d="M3.35149 12.0483L2.34744 13.0524V13.0512C2.26535 13.1311 2.21986 13.2398 2.21875 13.3541C2.21875 13.4673 2.26313 13.5771 2.34412 13.657C2.42399 13.738 2.53383 13.7824 2.64699 13.7824C2.76126 13.7812 2.86998 13.7358 2.94986 13.6548L3.95276 12.6496C4.03708 12.5709 4.08478 12.4621 4.08701 12.3468C4.08811 12.2314 4.04374 12.1204 3.96164 12.0394C3.88065 11.9585 3.76971 11.913 3.65434 11.9141C3.54007 11.9163 3.43025 11.964 3.35149 12.0483Z" fill="#6524EB" />
              <path d="M12.6535 12.0472C12.5737 11.964 12.4649 11.9163 12.3495 11.9141C12.2353 11.913 12.1243 11.9573 12.0434 12.0394C11.9613 12.1204 11.9169 12.2314 11.918 12.3456C11.9191 12.461 11.9679 12.5697 12.0511 12.6496L13.0563 13.6548L13.0552 13.6537C13.135 13.7358 13.2438 13.7824 13.358 13.7835C13.4723 13.7835 13.581 13.7391 13.662 13.6581C13.743 13.5771 13.7874 13.4684 13.7874 13.3541C13.7863 13.2398 13.7397 13.1311 13.6576 13.0512L12.6535 12.0472Z" fill="#6524EB" />
              <path d="M15.5726 7.57422H14.1526C13.9174 7.57422 13.7266 7.76504 13.7266 8.00024C13.7266 8.23654 13.9174 8.42626 14.1526 8.42626H15.5726C15.8078 8.42626 15.9987 8.23654 15.9987 8.00024C15.9987 7.76504 15.8078 7.57422 15.5726 7.57422Z" fill="#6524EB" />
              <path d="M13.0552 2.34339L12.0511 3.34743C11.9668 3.4262 11.9191 3.53603 11.918 3.65141C11.9169 3.76568 11.9613 3.87662 12.0434 3.9576C12.1243 4.03859 12.2353 4.08408 12.3495 4.08186C12.4649 4.08075 12.5737 4.03304 12.6535 3.94984L13.6576 2.9458C13.8162 2.77828 13.8129 2.51424 13.6487 2.35113C13.4856 2.18805 13.2227 2.18474 13.0552 2.34339Z" fill="#6524EB" />
              <path d="M8.42199 10.6159C8.2822 10.4761 8.0803 10.4139 7.88504 10.4528C7.68978 10.4916 7.52781 10.6259 7.45127 10.8089C7.37583 10.9931 7.39579 11.2027 7.50674 11.3681C7.61768 11.5334 7.80407 11.6321 8.00265 11.6321C8.16019 11.6332 8.31106 11.57 8.42202 11.4579C8.655 11.2261 8.65611 10.85 8.42424 10.6159L8.42199 10.6159Z" fill="#6524EB" />
              <path d="M7.95861 10.108C8.03848 10.1191 8.11947 10.098 8.18382 10.0492C8.24817 10.0004 8.29032 9.9283 8.30142 9.84842L8.68861 7.04934L8.68972 7.04823C8.69749 6.98499 8.69749 6.92064 8.68972 6.85741C8.63425 6.4791 8.28367 6.21616 7.90536 6.26831C7.52706 6.32156 7.2608 6.66993 7.3107 7.04824L7.69789 9.84732V9.84843C7.71675 9.98267 7.82326 10.0892 7.95861 10.108Z" fill="#6524EB" />
            </svg>

          </span>
          <p className={styles.footerText}>
            {isPayg ? (
              <>
                You have used <strong>{used} minutes</strong>, you will be charged{' '}
                <strong>${rate.toFixed(2)}/min</strong> (after free {freeMinutes} min)
              </>
            ) : (
              <>
                You have used <strong>{used} minutes</strong>, you will be charged{' '}
                <strong>${Number(monthlyFee).toFixed(2)}/month</strong>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  )
}

export default UsageCard
